/**
 * Grands Totaux perpétuels — SPEC §14.3 / R13 ENC-GTP-*.
 * Deux cumuls TTC, tenus PAR CAISSE, JAMAIS remis à zéro (y compris clôture
 * annuelle et changement d'exercice) :
 *  - GTPCA : cumul ALGÉBRIQUE (ventes +, retours/avoirs −) ;
 *  - GTPVA : cumul en VALEUR ABSOLUE (détecte les compensations frauduleuses :
 *    une vente annulée par un retour laisse GTPCA inchangé mais fait croître GTPVA).
 * S'y ajoutent les cumuls perpétuels PAR TAUX (HT/TVA/TTC) et le compteur de pièces.
 * Les clôtures (GTJ/GTM/GTA) sont des PHOTOGRAPHIES signées de ces cumuls à une
 * borne de période — la clôture n'écrit rien dans les cumuls, elle les fige.
 */
import { OPERATION_TYPE_CODES } from './piece-codes';
import type { OperationType } from './serialize';

export interface RateCumul {
  rateBp: number;
  ttcCents: number;
  htCents: number;
  vatCents: number;
}

export interface GrandTotals {
  /** GTPCA — cumul TTC perpétuel algébrique (centimes) */
  gtpcaCents: number;
  /** GTPVA — cumul TTC perpétuel en valeur absolue (centimes) */
  gtpvaCents: number;
  /** Compteur perpétuel de pièces scellées */
  pieceCount: number;
  /** Cumuls perpétuels par taux, triés par taux croissant */
  perRate: RateCumul[];
}

export const EMPTY_GRAND_TOTALS: GrandTotals = Object.freeze({
  gtpcaCents: 0,
  gtpvaCents: 0,
  pieceCount: 0,
  perRate: [],
});

/** Bloc de ventilation d'une pièce (forme de VatBlock de @orgazone/core-vat) */
export interface PieceVatBlock {
  rateBp: number;
  ttcCents: number;
  htCents: number;
  vatCents: number;
}

/**
 * UNE PIÈCE COMPTE-T-ELLE AUX GRANDS TOTAUX ? — LA règle, lue par tous ceux qui les calculent (le moteur au scellement,
 * la caisse qui les relit en base, le contrôle de cohérence des cumuls).
 * Non pour une opération de TRÉSORERIE : l'émission d'un titre (chèque cadeau, acompte) est un encaissement, pas du
 * chiffre d'affaires — référentiel § 6.18.1 : « les opérations de trésorerie ne doivent pas impacter les Grands-Totaux ».
 * Non plus pour l'ANNULATION d'une telle opération : elle retirerait des grands totaux un montant qui n'y est jamais entré.
 * ⚠️ SANS EFFET RÉTROACTIF, PAR CONSTRUCTION : la règle lit le type d'opération SIGNÉ. Les émissions de titres scellées
 *    avant elle portent `vente` et restent comptées — le compteur perpétuel ne baisse pas, les clôtures déjà signées
 *    restent justes.
 */
export function countsInGrandTotals(piece: { typeOperation: OperationType; cancelsTreasury?: boolean }): boolean {
  return piece.typeOperation !== 'tresorerie' && !piece.cancelsTreasury;
}

/** Le code PERSISTÉ d'une opération de trésorerie — pour qui juge une pièce STOCKÉE (une requête en base). */
export const TREASURY_OPERATION_CODE = OPERATION_TYPE_CODES.tresorerie;

/**
 * La pièce scellée appliquée aux cumuls selon `countsInGrandTotals` : hors grands totaux, elle ne compte que comme
 * maillon de la chaîne (le nombre de pièces avance, aucun montant ne bouge).
 */
export function applySealedPiece(
  gt: GrandTotals,
  piece: { typeOperation: OperationType; cancelsTreasury?: boolean; montantTtcCents: number; vatBlocks: PieceVatBlock[] },
): GrandTotals {
  if (countsInGrandTotals(piece)) return applyPieceToTotals(gt, piece.montantTtcCents, piece.vatBlocks);
  if (!Number.isInteger(piece.montantTtcCents)) throw new Error('core-nf525: montant TTC en centimes entiers attendu');
  return { ...gt, perRate: gt.perRate.map((r) => ({ ...r })), pieceCount: gt.pieceCount + 1 };
}

/** Applique une pièce scellée aux cumuls (IMMUTABLE — retourne un nouvel état) */
export function applyPieceToTotals(gt: GrandTotals, montantTtcCents: number, blocks: PieceVatBlock[]): GrandTotals {
  if (!Number.isInteger(montantTtcCents)) throw new Error('core-nf525: montant TTC en centimes entiers attendu');
  const perRate = new Map<number, RateCumul>(gt.perRate.map(r => [r.rateBp, { ...r }]));
  for (const b of blocks) {
    const cur = perRate.get(b.rateBp) ?? { rateBp: b.rateBp, ttcCents: 0, htCents: 0, vatCents: 0 };
    cur.ttcCents += b.ttcCents;
    cur.htCents += b.htCents;
    cur.vatCents += b.vatCents;
    perRate.set(b.rateBp, cur);
  }
  return {
    gtpcaCents: gt.gtpcaCents + montantTtcCents,
    gtpvaCents: gt.gtpvaCents + Math.abs(montantTtcCents),
    pieceCount: gt.pieceCount + 1,
    perRate: [...perRate.values()].sort((a, b) => a.rateBp - b.rateBp),
  };
}

/**
 * Deux cumuls perpétuels mis bout à bout — le PLANCHER d'une caisse reprise (ce que l'ancienne tablette a scellé sous ce
 * n° de caisse, relu au serveur) plus ce que la nouvelle a scellé depuis.
 * ⚠️ PAR TAUX AUSSI : le cumul global et sa ventilation doivent continuer ensemble, sinon la période suivante
 *    porterait un TTC juste et une ventilation fausse.
 */
export function addGrandTotals(a: GrandTotals, b: GrandTotals): GrandTotals {
  const perRate = new Map<number, RateCumul>(a.perRate.map((r) => [r.rateBp, { ...r }]));
  for (const r of b.perRate) {
    const cur = perRate.get(r.rateBp) ?? { rateBp: r.rateBp, ttcCents: 0, htCents: 0, vatCents: 0 };
    cur.ttcCents += r.ttcCents;
    cur.htCents += r.htCents;
    cur.vatCents += r.vatCents;
    perRate.set(r.rateBp, cur);
  }
  return {
    gtpcaCents: a.gtpcaCents + b.gtpcaCents,
    gtpvaCents: a.gtpvaCents + b.gtpvaCents,
    pieceCount: a.pieceCount + b.pieceCount,
    perRate: [...perRate.values()].sort((x, y) => x.rateBp - y.rateBp),
  };
}

/** Cumuls par taux → chaîne canonique `pb:HT:TVA;…` (taux croissants — même format que la ventilation pièce) */
export function formatCumulParTaux(gt: GrandTotals): string {
  return gt.perRate.map(r => `${r.rateBp}:${r.htCents}:${r.vatCents}`).join(';');
}

/** Chaîne canonique `pb:HT:TVA;…` → blocs (inverse exact de formatCumulParTaux ; '' = aucun). */
export function parseCumulParTaux(canonical: string): RateCumul[] {
  if (!canonical) return [];
  return canonical.split(';').map((part) => {
    const [pb, ht, tva] = part.split(':');
    const rateBp = parseInt(pb ?? '', 10);
    const htCents = parseInt(ht ?? '', 10);
    const vatCents = parseInt(tva ?? '', 10);
    if (!Number.isInteger(rateBp) || !Number.isInteger(htCents) || !Number.isInteger(vatCents)) {
      throw new Error(`core-nf525: cumul par taux invalide (${part})`);
    }
    return { rateBp, htCents, vatCents, ttcCents: htCents + vatCents };
  });
}

/** Ventilation d'une PÉRIODE (delta de cumuls perpétuels entre deux clôtures de même périodicité). */
export interface PeriodTotals {
  /** GT TTC de la période (R13 ENC-GTP-TTC) — delta algébrique du GTPCA */
  ttcCents: number;
  /** GT HT de la période (R13 ENC-GTP-HT) — Σ des deltas HT par taux */
  htCents: number;
  /** Ventilation de la période, canonique `pb:HT:TVA;…` — taux au delta entièrement nul OMIS */
  parTaux: string;
}

/**
 * Totaux de la PÉRIODE close = cumuls courants − cumuls photographiés par la clôture PRÉCÉDENTE de
 * MÊME périodicité (R13 [6] : mars se chaîne à février ; ENC-GTP-TTC/HT = totaux de la période).
 * Delta ALGÉBRIQUE : un retour dans la période sort négatif, comme partout ailleurs.
 */
export function periodTotalsSince(current: GrandTotals, prevCumulTtcCents: number, prevCumulParTaux: string): PeriodTotals {
  const prev = new Map(parseCumulParTaux(prevCumulParTaux).map(r => [r.rateBp, r]));
  const deltas: RateCumul[] = [];
  for (const r of current.perRate) {
    const p = prev.get(r.rateBp);
    const htCents = r.htCents - (p?.htCents ?? 0);
    const vatCents = r.vatCents - (p?.vatCents ?? 0);
    if (htCents !== 0 || vatCents !== 0) deltas.push({ rateBp: r.rateBp, htCents, vatCents, ttcCents: htCents + vatCents });
  }
  return {
    ttcCents: current.gtpcaCents - prevCumulTtcCents,
    htCents: deltas.reduce((s, d) => s + d.htCents, 0),
    parTaux: deltas.map(d => `${d.rateBp}:${d.htCents}:${d.vatCents}`).join(';'),
  };
}

// ─── Identifiants de période DATÉS non équivoques (R13 ENC-GTP-ORI-NID) ───────

const DAY_RE = /^\d{8}$/;
const MONTH_RE = /^\d{6}$/;

/** Période de clôture journalière : `J-AAAAMMJJ` */
export function dayPeriodId(dayYmd: string): string {
  if (!DAY_RE.test(dayYmd)) throw new Error(`core-nf525: jour AAAAMMJJ attendu (${dayYmd})`);
  return `J-${dayYmd}`;
}

/** Période de clôture mensuelle : `M-AAAAMM` */
export function monthPeriodId(monthYm: string): string {
  if (!MONTH_RE.test(monthYm)) throw new Error(`core-nf525: mois AAAAMM attendu (${monthYm})`);
  return `M-${monthYm}`;
}

/** Période de clôture d'exercice : `E-AAAAMMJJ-AAAAMMJJ` (bornes incluses) */
export function exercisePeriodId(startYmd: string, endYmd: string): string {
  if (!DAY_RE.test(startYmd) || !DAY_RE.test(endYmd)) throw new Error('core-nf525: bornes AAAAMMJJ attendues');
  if (endYmd < startYmd) throw new Error('core-nf525: borne de fin antérieure au début');
  return `E-${startYmd}-${endYmd}`;
}

// ─── Exercice fiscal & clôtures « au prochain usage » (SPEC retail §9.4.A) ────

/** Jours par mois — février PLAFONNÉ À 28 (décision réglages §12 : borne fixe, pas de 29) */
export function clampClosingDay(day: number, month: number): number {
  const max = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1] ?? 31;
  return Math.min(Math.max(1, Math.trunc(day)), max);
}

export interface ExerciseBounds {
  /** Premier jour de l'exercice courant (AAAAMMJJ) */
  startYmd: string;
  /** Dernier jour de l'exercice courant (AAAAMMJJ, inclus) */
  endYmd: string;
}

const ymd = (y: number, m: number, d: number): string => `${y}${String(m).padStart(2, '0')}${String(d).padStart(2, '0')}`;

/**
 * Bornes de l'exercice fiscal CONTENANT refYmd, pour une fin d'exercice
 * paramétrée (jour + mois de clôture, réglages Société §12).
 * Miroir exact du calcul affiché à l'écran Réglages (une seule vérité).
 */
export function computeExercise(closingDay: number, closingMonth: number, refYmd: string): ExerciseBounds {
  if (!DAY_RE.test(refYmd)) throw new Error(`core-nf525: date de référence AAAAMMJJ attendue (${refYmd})`);
  const refYear = parseInt(refYmd.slice(0, 4), 10);
  const endThisYear = ymd(refYear, closingMonth, clampClosingDay(closingDay, closingMonth));
  const endYmd = refYmd <= endThisYear ? endThisYear : ymd(refYear + 1, closingMonth, clampClosingDay(closingDay, closingMonth));
  const endYear = parseInt(endYmd.slice(0, 4), 10);
  // début = lendemain de la clôture précédente (même jour/mois, un an avant)
  const prevEnd = ymd(endYear - 1, closingMonth, clampClosingDay(closingDay, closingMonth));
  const startYmd = nextDay(prevEnd);
  return { startYmd, endYmd };
}

/** Lendemain d'une date AAAAMMJJ (calendrier grégorien réel) */
export function nextDay(dayYmd: string): string {
  if (!DAY_RE.test(dayYmd)) throw new Error(`core-nf525: jour AAAAMMJJ attendu (${dayYmd})`);
  const d = new Date(parseInt(dayYmd.slice(0, 4), 10), parseInt(dayYmd.slice(4, 6), 10) - 1, parseInt(dayYmd.slice(6, 8), 10));
  d.setDate(d.getDate() + 1);
  return ymd(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

export interface PendingClosingsInput {
  /** Dernier jour AYANT EU de l'activité scellée (AAAAMMJJ) — un jour sans vente n'a rien à clôturer */
  lastActivityDay: string;
  /** Dernier jour déjà clôturé (Z), AAAAMMJJ — '' si jamais */
  lastClosedDay: string;
  /** Dernier mois déjà clôturé, AAAAMM — '' si jamais */
  lastClosedMonth: string;
  /** Borne de fin du dernier exercice déjà clôturé, AAAAMMJJ — '' si jamais */
  lastClosedExerciseEnd: string;
  /** Paramétrage fin d'exercice (réglages Société) */
  closingDay: number;
  closingMonth: number;
  /**
   * Exercice EXCEPTIONNEL (§12.10 — création d'entreprise, changement de date de clôture) :
   * bornes ≠ 12 mois qui REMPLACENT la règle récurrente pour l'activité tombant dans la fenêtre.
   * Sans lui, un exercice de transition (ex. 18 mois) verrait une GTA scellée À TORT à la borne
   * récurrente intermédiaire — l'exercice n'y est pas échu. Après sa clôture, la récurrence reprend.
   */
  exceptionalExercise?: { startYmd: string; endYmd: string };
  /** Jour courant (heure de confiance produit), AAAAMMJJ */
  todayYmd: string;
}

export interface PendingClosing {
  type: 'GTJ' | 'GTM' | 'GTA';
  periodeId: string;
}

/**
 * Clôtures à produire AVANT toute nouvelle vente (« clôture par l'usage », §9.4.A) :
 * au premier usage d'un nouveau jour/mois/exercice, les clôtures manquées sont
 * générées dans l'ordre chronologique GTJ → GTM → GTA, ARRÊTÉES À LA BORNE de
 * leur période (les cumuls signés sont ceux au moment de la dernière activité).
 */
export function pendingClosings(input: PendingClosingsInput): PendingClosing[] {
  const out: PendingClosing[] = [];
  const { lastActivityDay, todayYmd } = input;
  if (!DAY_RE.test(lastActivityDay) || !DAY_RE.test(todayYmd)) throw new Error('core-nf525: dates AAAAMMJJ attendues');

  // GTJ : le dernier jour d'activité est passé et n'a pas été clôturé
  if (lastActivityDay < todayYmd && input.lastClosedDay < lastActivityDay) {
    out.push({ type: 'GTJ', periodeId: dayPeriodId(lastActivityDay) });
  }
  // GTM : le mois du dernier jour d'activité est révolu et n'a pas été clôturé
  const activityMonth = lastActivityDay.slice(0, 6);
  if (activityMonth < todayYmd.slice(0, 6) && input.lastClosedMonth < activityMonth) {
    out.push({ type: 'GTM', periodeId: monthPeriodId(activityMonth) });
  }
  // GTA : l'exercice contenant la dernière activité est échu et n'a pas été clôturé.
  // L'exercice EXCEPTIONNEL prime sur la récurrence quand l'activité tombe dans sa fenêtre
  // (bornes de transition ≠ 12 mois) ; hors fenêtre, la règle récurrente reprend.
  const exc = input.exceptionalExercise;
  if (exc && (!DAY_RE.test(exc.startYmd) || !DAY_RE.test(exc.endYmd) || exc.endYmd < exc.startYmd)) {
    throw new Error('core-nf525: bornes AAAAMMJJ d\'exercice exceptionnel invalides');
  }
  const ex =
    exc && lastActivityDay >= exc.startYmd && lastActivityDay <= exc.endYmd
      ? { startYmd: exc.startYmd, endYmd: exc.endYmd }
      : computeExercise(input.closingDay, input.closingMonth, lastActivityDay);
  if (ex.endYmd < todayYmd && input.lastClosedExerciseEnd < ex.endYmd) {
    out.push({ type: 'GTA', periodeId: exercisePeriodId(ex.startYmd, ex.endYmd) });
  }
  return out;
}
